if (!customElements.get("scrolling-text")) {
  class ScrollingText extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      this.stop();
      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      window.removeEventListener("blur", this._onBlur);
      window.removeEventListener("focus", this._onFocus);

      this.removeEventListener("mouseover", this._onMouseOver);
      this.removeEventListener("mouseout", this._onMouseOut);

      if (this._dragEnabled) {
        if (this._momentumRAF) cancelAnimationFrame(this._momentumRAF);
        this.removeEventListener("pointerdown", this._onPointerDown);
        this.removeEventListener("click", this._onClickCapture, true);
        this.removeEventListener("dragstart", this._onDragStart);
        window.removeEventListener("pointermove", this._onPointerMove);
        window.removeEventListener("pointerup", this._onPointerUp);
      }
    }

    init() {
      this._running = false;
      this._position = 0;

      this._speed = this._getSpeed();

      this._direction = this.dataset.scrollingDirection || "ltr";
      this._pauseOnHover = this.dataset.pauseOnHover === "true";
      this._dragEnabled = this.dataset.dragEnabled === "true";

      this._lastTime = null;

      this._loop = this._loop.bind(this);
      this._onMouseOver = this._onMouseOver.bind(this);
      this._onMouseOut = this._onMouseOut.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
      this._onDragStart = this._onDragStart.bind(this);
      this._momentumStep = this._momentumStep.bind(this);

      this._base = this.children[0];
      if (!this._base) return;

      this._base.style.position = "relative";

      for (const child of this.children) {
        child.style.willChange = "transform";
        child.style.transform = "translate3d(0,0,0)";
      }

      this._template = this._base.cloneNode(true);

      this._readSizes();
      this._setupChildren();

      this._setupHoverPause();
      this._setupDrag();
      this._setupIntersectionObserver();
      this._setupResizeObserver();

      this.start();
    }

    _readSizes() {
      const rectBox = this.getBoundingClientRect();
      const rectChild = this._base.getBoundingClientRect();
      this._boxWidth = rectBox.width;
      this._itemWidth = rectChild.width;
    }

    _neededChildren() {
      return Math.ceil(this._boxWidth / this._itemWidth) + 1;
    }

    _setupChildren() {
      const needed = this._neededChildren();
      const current = this.children.length;

      if (needed > current) {
        for (let i = current; i < needed; i++) {
          const clone = this._template.cloneNode(true);
          clone.style.willChange = "transform";
          clone.style.transform = "translate3d(0,0,0)";
          this.appendChild(clone);
        }
      } else if (needed < current) {
        for (let i = current; i > needed; i--) {
          this.removeChild(this.lastElementChild);
        }
      }
    }

    start() {
      if (this._running) return;
      this._running = true;
      this._lastTime = null;
      requestAnimationFrame(this._loop);
    }

    _getSpeed() {
      const desktopSpeed = parseFloat(this.dataset.scrollingSpeed);
      const speed = Number.isFinite(desktopSpeed) ? desktopSpeed : 0;

      if (window.innerWidth > 768) return speed;

      const mobileSpeed = parseFloat(this.dataset.scrollingSpeedMobile);
      return Number.isFinite(mobileSpeed) ? mobileSpeed : speed / 1.5;
    }

    stop() {
      this._running = false;
    }

    _loop(timestamp) {
      if (!this._running) return;

      if (!this._lastTime) this._lastTime = timestamp;
      const delta = timestamp - this._lastTime;
      this._lastTime = timestamp;

      this._render();

      this._position += (this._speed * delta) / 1000;
      if (this._position >= this._itemWidth) {
        this._position = this._position % this._itemWidth;
      }

      requestAnimationFrame(this._loop);
    }

    _render() {
      const p = this._position;
      const dir = this._direction;

      const transformValue =
        dir === "rtl"
          ? `translate3d(${p}px, 0, 0)`
          : `translate3d(${-p}px, 0, 0)`;

      for (const el of this.children) {
        el.style.transform = transformValue;
      }
    }

    _normalizePosition(position) {
      if (!this._itemWidth) return position;
      let p = position % this._itemWidth;
      if (p < 0) p += this._itemWidth;
      return p;
    }

    _setupHoverPause() {
      if (!this._pauseOnHover) return;

      this._windowInFocus = true;

      this._onBlur = () => (this._windowInFocus = false);
      this._onFocus = () => (this._windowInFocus = true);

      window.addEventListener("blur", this._onBlur);
      window.addEventListener("focus", this._onFocus);

      this.addEventListener("mouseover", this._onMouseOver);
      this.addEventListener("mouseout", this._onMouseOut);
    }

    _onMouseOver() {
      if (this._windowInFocus) this.stop();
    }
    _onMouseOut() {
      if (this._windowInFocus) this.start();
    }

    _setupDrag() {
      if (!this._dragEnabled) return;

      this._dragThreshold = 6;
      this._isDragging = false;
      this._dragMoved = false;
      this._preventClickAfterDrag = false;
      this._dragStartX = 0;
      this._lastPointerX = 0;
      this._lastMoveTime = 0;
      this._pointerVelocity = 0;
      this._dragVelocity = 0;
      this._wasRunning = false;

      this.classList.add("scrolling-images--draggable");
      this.style.cursor = "grab";
      this.style.userSelect = "none";
      this.style.touchAction = "pan-y";

      this.addEventListener("pointerdown", this._onPointerDown);
      this.addEventListener("click", this._onClickCapture, true);
      // prevent native image/link ghost dragging so our drag stays smooth
      this.addEventListener("dragstart", this._onDragStart);
    }

    _onDragStart(e) {
      e.preventDefault();
    }

    _onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;

      if (this._momentumRAF) {
        cancelAnimationFrame(this._momentumRAF);
        this._momentumRAF = null;
      }

      this._isDragging = true;
      this._dragMoved = false;
      this._dragStartX = e.clientX;
      this._lastPointerX = e.clientX;
      this._lastMoveTime = performance.now();
      this._pointerVelocity = 0;
      this._wasRunning = this._running;

      this.stop();
      this.classList.add("scrolling-images--dragging");
      this.style.cursor = "grabbing";

      try {
        this.setPointerCapture(e.pointerId);
      } catch (err) {}

      window.addEventListener("pointermove", this._onPointerMove);
      window.addEventListener("pointerup", this._onPointerUp);
    }

    _onPointerMove(e) {
      if (!this._isDragging) return;

      if (Math.abs(e.clientX - this._dragStartX) > this._dragThreshold) {
        this._dragMoved = true;
        this._preventClickAfterDrag = true;
      }

      const deltaX = e.clientX - this._lastPointerX;
      const now = performance.now();
      const dt = now - this._lastMoveTime;
      if (dt > 0) this._pointerVelocity = deltaX / dt;
      this._lastPointerX = e.clientX;
      this._lastMoveTime = now;

      // rtl content follows the pointer, ltr moves opposite
      const positionDelta = this._direction === "rtl" ? deltaX : -deltaX;
      this._position = this._normalizePosition(this._position + positionDelta);
      this._render();
    }

    _onPointerUp(e) {
      if (!this._isDragging) return;
      this._isDragging = false;

      this.classList.remove("scrolling-images--dragging");
      this.style.cursor = "grab";

      try {
        this.releasePointerCapture(e.pointerId);
      } catch (err) {}

      window.removeEventListener("pointermove", this._onPointerMove);
      window.removeEventListener("pointerup", this._onPointerUp);

      // carry the flick into a decaying glide before auto-scroll resumes
      this._dragVelocity =
        this._direction === "rtl"
          ? this._pointerVelocity
          : -this._pointerVelocity;
      this._momentumLast = performance.now();
      this._momentumRAF = requestAnimationFrame(this._momentumStep);
    }

    _momentumStep(timestamp) {
      const delta = timestamp - this._momentumLast;
      this._momentumLast = timestamp;

      // px per ms, eased down each frame
      this._position = this._normalizePosition(
        this._position + this._dragVelocity * delta,
      );
      this._render();

      this._dragVelocity *= Math.pow(0.95, delta / 16);

      if (Math.abs(this._dragVelocity) > 0.015) {
        this._momentumRAF = requestAnimationFrame(this._momentumStep);
      } else {
        this._momentumRAF = null;
        if (this._wasRunning) this.start();
      }
    }

    _onClickCapture(e) {
      if (!this._preventClickAfterDrag) return;
      e.preventDefault();
      e.stopPropagation();
      this._preventClickAfterDrag = false;
    }

    _setupIntersectionObserver() {
      this._intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) this.start();
        else this.stop();
      });
      this._intersectionObserver.observe(this);
    }

    _setupResizeObserver() {
      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this);
    }

    _onResize() {
      this._speed = this._getSpeed();
      if (!this._running) return;
      this._readSizes();
      this._setupChildren();
    }
  }

  customElements.define("scrolling-text", ScrollingText);
}
